// mvp-returns Phase F1 — Return analytics client. Endpoints live on
// de-order-service: /ord/return-analytics/*.

import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface IAnalyticsDashboard {
  window: { dateFrom: string; dateTo: string };
  counts: {
    total: number;
    active: number;
    completed: number;
    rejected: number;
    cancelled: number;
    disputed: number;
    refundPending: number;
    // §T1b — returns awaiting staff action (REQUESTED/PROPOSED/DISPUTED/AWAITING_RESOLUTION).
    staffPending: number;
    consignment: number;
    receiveTime: number;
  };
  money: { totalRequested: number; totalRefunded: number; currency: string };
  // §T1b — returnRate = totalRequested / sales for the window (0..1).
  rates: { completionRate: number; rejectionRate: number; returnRate: number };
  avgResolutionSeconds: number;
}

export interface IDateRangeParams {
  dateFrom?: string;
  dateTo?: string;
}

export const dashboard = (params?: IDateRangeParams) =>
  httpRequest.get("/ord/return-analytics/dashboard", params as any) as Promise<IAnalyticsDashboard>;

// ─── Breakdown ────────────────────────────────────────────────────────────────

export type BreakdownDimension =
  | "reason"
  | "resolution"
  | "refund-method"
  | "request-type"
  | "status";

export interface IBreakdownBucket {
  bucket: string | null;
  count: number;
  refundAmount: number;
}

export interface IBreakdownResponse {
  dimension: string;
  window: { dateFrom: string; dateTo: string };
  buckets: IBreakdownBucket[];
}

export interface IBreakdownParams extends IDateRangeParams {
  dimension: BreakdownDimension;
}

export const breakdown = (params: IBreakdownParams) =>
  httpRequest.get("/ord/return-analytics/breakdown", params as any) as Promise<IBreakdownResponse>;

// ─── Repeat Returners ─────────────────────────────────────────────────────────

export interface IRepeatReturner {
  buyerBusinessId: string;
  buyerBusinessName: string | null;
  returnCount: number;
  totalRefundAmount: number;
  lastReturnedAt: string | null;
}

export interface IRepeatReturnersResponse {
  threshold: number;
  window: { dateFrom: string; dateTo: string };
  buyers: IRepeatReturner[];
}

export interface IRepeatReturnersParams extends IDateRangeParams {
  threshold?: number;
  limit?: number;
}

export const repeatReturners = (params?: IRepeatReturnersParams) =>
  httpRequest.get("/ord/return-analytics/repeat-returners", params as any) as Promise<IRepeatReturnersResponse>;

// ─── Staff SLA ────────────────────────────────────────────────────────────────

export interface IStaffSlaRow {
  userId              : string;
  actions             : number;
  acceptedCount       : number;
  rejectedCount       : number;
  completedCount      : number;
  avgResponseSeconds  : number;
  avgResolutionSeconds: number;
}

export interface IStaffSlaResponse {
  window: { dateFrom: string; dateTo: string };
  staff : IStaffSlaRow[];
}

export interface IStaffSlaParams extends IDateRangeParams {
  limit?: number;
}

export const staffSla = (params?: IStaffSlaParams) =>
  httpRequest.get("/ord/return-analytics/staff-sla", params as any) as Promise<IStaffSlaResponse>;

// ─── Resolution Histogram ─────────────────────────────────────────────────────

export interface IResolutionHistogramBucket {
  key  : string;
  label: string;
  count: number;
}

export interface IResolutionHistogramResponse {
  window : { dateFrom: string; dateTo: string };
  buckets: IResolutionHistogramBucket[];
}

export const resolutionHistogram = (params?: IDateRangeParams) =>
  httpRequest.get("/ord/return-analytics/resolution-histogram", params as any) as Promise<IResolutionHistogramResponse>;

// ─── Trend ────────────────────────────────────────────────────────────────────

export interface ITrendPoint {
  bucketStart : string;
  count       : number;   // returns CREATED in this bucket
  resolved    : number;   // of those created, now COMPLETED (cohort-by-created-date)
  disputed    : number;   // of those created, ever disputed (cohort-by-created-date)
  refundAmount: number;
}

export interface ITrendResponse {
  bucket: "day" | "week";
  window: { dateFrom: string; dateTo: string };
  points: ITrendPoint[];
}

export interface ITrendParams extends IDateRangeParams {
  bucket?: "day" | "week";
}

export const trend = (params?: ITrendParams) =>
  httpRequest.get("/ord/return-analytics/trend", params as any) as Promise<ITrendResponse>;

// ─── Top Products ─────────────────────────────────────────────────────────────

export interface ITopProduct {
  productId    : string | null;
  productName  : string;
  sku          : string | null;
  returnCount  : number;
  totalQuantity: number;
  refundAmount : number;
}

export interface ITopProductsResponse {
  window  : { dateFrom: string; dateTo: string };
  products: ITopProduct[];
}

export interface ITopProductsParams extends IDateRangeParams {
  limit?: number;
}

export const topProducts = (params?: ITopProductsParams) =>
  httpRequest.get("/ord/return-analytics/top-products", params as any) as Promise<ITopProductsResponse>;

// ─── Comparable Returns ───────────────────────────────────────────────────────
// §9.2 §8 — the seller's own COMPLETED returns of the same product + reason,
// resolved in the last 90d, across buyers; plus a median refund% suggestion.

export interface IComparableReturn {
  returnRequestId  : string;
  returnNumber     : string;
  buyerBusinessId  : string;
  buyerBusinessName: string | null;
  refundPercent    : number | null;
  resolutionType   : string | null;
  status           : string;
  resolvedTimeDays : number | null;
}

export interface IComparableReturnsResponse {
  window             : { dateFrom: string; dateTo: string };
  productId          : string;
  returnReason       : string;
  count              : number;
  medianRefundPercent: number;
  comparables        : IComparableReturn[];
}

export interface IComparableParams {
  productId: string;
  returnReason: string;
  excludeReturnId?: string;
}

export const comparable = (params: IComparableParams) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/return-analytics/comparable", params as any) as Promise<IComparableReturnsResponse>;
